import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import logo from './assets/logo.png';
import aiproject from './assets/aiproject.png';
import greenIcon from './assets/green_icon.png';
import report from './assets/Phase2_Report.pdf';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  Button,
  Link
} from '@mui/material';
import {
  GitHub
} from '@mui/icons-material';

const climateQuestDescription = (
  <Box>
    <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.6, mb: 2 }}>
      A gamified carbon footprint tracker built solo. Users complete AI-generated daily eco-quests
      tailored to their habits, earning XP and coins to level up an RPG avatar — behavior change
      psychology meets fantasy game mechanics.
    </Typography>

    <Typography variant="body2" sx={{ color: '#444', fontWeight: 'bold', mb: 0.5 }}>
      Tech Stack
    </Typography>
    <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6, mb: 2 }}>
      Java (Android) · Firebase Auth · Cloud Firestore · Firebase Cloud Functions (Node.js) ·
      Anthropic Claude API · Firebase Cloud Messaging · Google News RSS · MVVM (ViewModel + LiveData)
    </Typography>

    <Typography variant="body2" sx={{ color: '#444', fontWeight: 'bold', mb: 0.5 }}>
      Implemented
    </Typography>
    <Box component="ul" sx={{ m: 0, pl: 2.5, color: '#666' }}>
      {[
        'Google OAuth + Firebase Auth; 10-question onboarding quiz personalizes AI quest generation',
        'MVVM with real-time Firestore snapshot listeners',
        'Firebase Cloud Functions (Node.js, Gen 2) — Claude API called server-side only',
        'Claude Haiku generates 3–5 personalized eco-quests daily in JSON; stale detection triggers refresh on login',
        'XP and coin rewards evaluated server-side via Firestore trigger; client-side optimistic updates',
        'Social: friend requests, activity feeds, and like/reaction system on completed quests',
        'Shop with cosmetic items purchasable with coins; inventory persisted to Firestore',
        'Climate news feed from Google News RSS with Chrome Custom Tabs integration',
        'Class system (Ranger / Warrior / Mage) with character avatar and equipped item display',
      ].map((item, i) => (
        <Typography key={i} component="li" variant="body2" sx={{ lineHeight: 1.6, mb: 0.5 }}>
          {item}
        </Typography>
      ))}
    </Box>

    <Typography variant="body2" sx={{ color: '#444', fontWeight: 'bold', mt: 2, mb: 0.5 }}>
      Planned / In Progress
    </Typography>
    <Box component="ul" sx={{ m: 0, pl: 2.5, color: '#666' }}>
      {[
        'CO2 savings estimate per quest with lifetime total on profile',
        'Layered pixel art character renderer with per-class base sprite and transparent equipment layers',
        'Three equipment slots (armor, weapon, accessory) with class restrictions, level gates, and daily shop rotation via Cloud Scheduler',
        'Consumable items (e.g. Double XP Potion) with server-side effect evaluation',
        'Push notifications via Firebase Cloud Messaging — quest reminders, friend requests, and like alerts (Android 13+ permission flow)',
        'Day streak tracking on home screen',
        'Quest deduplication using recent quest history passed to Claude',
        'Ambient background music via a bound Android Service',
      ].map((item, i) => (
        <Typography key={i} component="li" variant="body2" sx={{ lineHeight: 1.6, mb: 0.5 }}>
          {item}
        </Typography>
      ))}
    </Box>
  </Box>
);

const tarnishedTacticsDescription = (
  <Box>
    <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.6, mb: 2 }}>
      A full-stack web app for Elden Ring players to create and share character builds and
      AI-generated strategy guides.
    </Typography>

    <Typography variant="body2" sx={{ color: '#444', fontWeight: 'bold', mb: 0.5 }}>
      Tech Stack
    </Typography>
    <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6, mb: 2 }}>
      React · Node.js · Express · MongoDB · Google Cloud App Engine · OpenAI API · Google OAuth 2.0
    </Typography>

    <Typography variant="body2" sx={{ color: '#444', fontWeight: 'bold', mb: 0.5 }}>
      Implemented
    </Typography>
    <Box component="ul" sx={{ m: 0, pl: 2.5, color: '#666' }}>
      {[
        'Google OAuth 2.0 authentication',
        'Build creator with all 8 core stats (VIG, MND, END, STR, DEX, INT, FAI, ARC), equipment slots, tags, and real-time level calculation',
        'Grid and list view modes for browsing community and preset builds',
        'AI strategy guide generation via OpenAI API — analyzes build stats and equipment to produce combat, progression, and gear tips in one click',
        'Full CRUD for builds and guides with public/private visibility controls',
        'Build sharing via unique URLs; public builds appear in the community library',
      ].map((item, i) => (
        <Typography key={i} component="li" variant="body2" sx={{ lineHeight: 1.6, mb: 0.5 }}>
          {item}
        </Typography>
      ))}
    </Box>
  </Box>
);

const networkDbDescription = (
  <Box>
    <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.6, mb: 2 }}>
      A multi-threaded networked database server supporting read, write, and delete operations
      over TCP. Stores up to 200 keys with values up to 4096 bytes.
    </Typography>

    <Typography variant="body2" sx={{ color: '#444', fontWeight: 'bold', mb: 0.5 }}>
      Tech Stack
    </Typography>
    <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6, mb: 2 }}>
      C · POSIX Threads (pthreads) · TCP Sockets · Mutexes / Semaphores · Makefile
    </Typography>

    <Typography variant="body2" sx={{ color: '#444', fontWeight: 'bold', mb: 0.5 }}>
      Implemented
    </Typography>
    <Box component="ul" sx={{ m: 0, pl: 2.5, color: '#666' }}>
      {[
        'Three-tier thread architecture: main thread handles stats/quit commands, listener thread accepts TCP connections and enqueues requests, four worker threads process the queue concurrently',
        'Thread-safe work queue (queue.c) coordinating requests between the listener and workers',
        'Key-value storage module (database.c) managing up to 200 entries with per-key locking',
        'TCP socket server handling SET, GET, and DELETE operations from connected clients',
        'Test client (dbtest.c) for verifying read, write, and delete behavior',
      ].map((item, i) => (
        <Typography key={i} component="li" variant="body2" sx={{ lineHeight: 1.6, mb: 0.5 }}>
          {item}
        </Typography>
      ))}
    </Box>
  </Box>
);

const studentSuccessDescription = (
  <Box>
    <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.6, mb: 2 }}>
      A machine learning system that identifies students at risk of academic failure using
      ensemble methods, achieving ROC-AUC of 0.7135 and F1 score of 0.4103.
    </Typography>

    <Typography variant="body2" sx={{ color: '#444', fontWeight: 'bold', mb: 0.5 }}>
      Tech Stack
    </Typography>
    <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6, mb: 2 }}>
      Python · scikit-learn · pandas · NumPy · Matplotlib · Jupyter Notebook
    </Typography>

    <Typography variant="body2" sx={{ color: '#444', fontWeight: 'bold', mb: 0.5 }}>
      Implemented
    </Typography>
    <Box component="ul" sx={{ m: 0, pl: 2.5, color: '#666' }}>
      {[
        'From-scratch Random Forest classifier and Gradient Boosting model via scikit-learn',
        'Feature selection using importance analysis to identify the strongest predictors',
        'Model fusion via weighted averaging across multiple classifiers',
        'Stacking ensemble with a meta-learner for final predictions',
        'Key finding: behavioral features (absences, past failures) outperform demographic features as predictors of student success',
      ].map((item, i) => (
        <Typography key={i} component="li" variant="body2" sx={{ lineHeight: 1.6, mb: 0.5 }}>
          {item}
        </Typography>
      ))}
    </Box>
  </Box>
);

const unixFsDescription = (
  <Box>
    <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.6, mb: 2 }}>
      A read-write Unix-like filesystem implemented in user space using the FUSE library,
      supporting standard file and directory operations mountable on Linux.
    </Typography>

    <Typography variant="body2" sx={{ color: '#444', fontWeight: 'bold', mb: 0.5 }}>
      Tech Stack
    </Typography>
    <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6, mb: 2 }}>
      C · FUSE (libfuse) · Unix/Linux · Makefile
    </Typography>

    <Typography variant="body2" sx={{ color: '#444', fontWeight: 'bold', mb: 0.5 }}>
      Implemented
    </Typography>
    <Box component="ul" sx={{ m: 0, pl: 2.5, color: '#666' }}>
      {[
        'Core FUSE interface callbacks: getattr, readdir, open, read, write, create, unlink, mkdir, rmdir, and truncate',
        'File and directory metadata management tracking permissions, size, and timestamps',
        'Persistent storage of file contents and directory structure',
        'Incremental unit testing of each operation to verify correctness before building on top',
      ].map((item, i) => (
        <Typography key={i} component="li" variant="body2" sx={{ lineHeight: 1.6, mb: 0.5 }}>
          {item}
        </Typography>
      ))}
    </Box>
  </Box>
);

function Projects() {
  const projects = [
    {
      id: 1,
      title: "Climate Quest — Full Stack Android Application",
      description: climateQuestDescription,
      link: "https://github.com/samhull99/guild",
      imageType: "climatequest",
      linkText: "View on GitHub"
    },
    {
      id: 2,
      title: "Tarnished Tactics",
      description: tarnishedTacticsDescription,
      link: "https://tarnished-tactics-frontend.uc.r.appspot.com/",
      imageType: "tarnished",
      linkText: "Link to Tarnished Tactics"
    },
    {
      id: 3,
      title: "Student Success Prediction System",
      description: studentSuccessDescription,
      link: report,
      imageType: "aiproject",
      linkText: "Download Report",
      isDownload: true
    },
    {
      id: 4,
      title: "Unix Read-Write File System",
      description: unixFsDescription,
      link: "https://github.com/samhull99/Systems-Projects/tree/main/project3",
      imageType: "github",
      linkText: "View on GitHub"
    },
    {
      id: 5,
      title: "Simple Network Database Server",
      description: networkDbDescription,
      link: "https://github.com/samhull99/Systems-Projects/tree/main/project2",
      imageType: "github",
      linkText: "View on GitHub"
    }
  ];

  const renderProjectImage = (project) => {
    if (project.imageType === "tarnished") {
      return (
        <Box sx={{
          width: { xs: '100%', md: 300 },
          height: 300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          overflow: 'hidden',
          backgroundColor: '#f5f5f5'
        }}>
          <img
            src={logo}
            alt="Tarnished Tactics Logo"
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain'
            }}
          />
        </Box>
      );
    } else if (project.imageType === "aiproject") {
      return (
        <Box sx={{
          width: { xs: '100%', md: 300 },
          height: 300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          overflow: 'hidden',
          backgroundColor: '#f5f5f5'
        }}>
          <img
            src={aiproject}
            alt="Student Success Prediction System"
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain'
            }}
          />
        </Box>
      );
    } else if (project.imageType === "climatequest") {
      return (
        <Box sx={{
          width: { xs: '100%', md: 300 },
          minHeight: 300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          overflow: 'hidden',
          backgroundColor: '#f5f5f5'
        }}>
          <img
            src={greenIcon}
            alt="Climate Quest"
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain'
            }}
          />
        </Box>
      );
    } else {
      return (
        <Box sx={{
          width: { xs: '100%', md: 300 },
          height: 300,
          backgroundColor: '#24292e',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <GitHub sx={{ fontSize: '4rem', color: 'white' }} />
        </Box>
      );
    }
  };

  return (
    <>
      {/* Navigation Bar */}
      <AppBar position="fixed" sx={{ backgroundColor: '#A30000' }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Samuel Hull
          </Typography>
          <Button color="inherit" component={RouterLink} to="/">Home</Button>
          <Button color="inherit" component={RouterLink} to="/about">About</Button>
          <Button color="inherit" component={RouterLink} to="/projects">Projects</Button>
          <Button color="inherit" component={RouterLink} to="/contact">Contact</Button>
        </Toolbar>
      </AppBar>

      {/* Projects Content */}
      <Container maxWidth="lg" sx={{ mt: 10 }}>
        <Box sx={{ py: 4 }}>
          <Typography
            variant="h3"
            component="h1"
            textAlign="center"
            gutterBottom
            sx={{ mb: 6, fontWeight: 'bold', color: '#A30000' }}
          >
            My Projects
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {projects.map((project) => (
              <Card
                key={project.id}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4
                  },
                  overflow: 'hidden'
                }}
              >
                {renderProjectImage(project)}

                <CardContent sx={{
                  flex: 1,
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}>
                  <Typography
                    variant="h4"
                    component="h2"
                    gutterBottom
                    sx={{ fontWeight: 'bold', color: '#333' }}
                  >
                    {project.title}
                  </Typography>

                  {typeof project.description === 'string' ? (
                    <Typography
                      variant="body1"
                      sx={{ mb: 3, color: '#666', lineHeight: 1.6 }}
                    >
                      {project.description}
                    </Typography>
                  ) : (
                    <Box sx={{ mb: 3 }}>
                      {project.description}
                    </Box>
                  )}

                  <Link
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={project.isDownload ? "Phase2_Report.pdf" : undefined}
                    sx={{
                      color: '#A30000',
                      textDecoration: 'none',
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                      '&:hover': {
                        color: '#8B0000',
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    {project.linkText} →
                  </Link>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      </Container>
    </>
  );
}

export default Projects;
